import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Check,
    Clock,
    Globe,
    Link as LinkIcon,
    Loader2,
    Lock,
    PiggyBank,
    Radio,
    Rocket,
    Scale,
    Sparkles,
    Users,
} from "lucide-react";
import { api } from "@/lib/api";
import TrafficLightBadge from "@/components/common/TrafficLightBadge";
import OverrideReasonModal from "@/components/common/OverrideReasonModal";

const fmt = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
        });
    } catch {
        return iso;
    }
}

function remainingHours(iso) {
    if (!iso) return 0;
    return Math.max(0, (new Date(iso).getTime() - Date.now()) / 36e5);
}

// =============================================================
// 1. PRICING OVERRIDE LOG · read-only audit (no approval queue)
// =============================================================
function PricingOverrideLog() {
    const [entries, setEntries] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/pricing/override-log");
            setEntries(data.entries);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load pricing log");
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const overrides = (entries || []).filter((e) => e.tone === "red");
    const yellows = (entries || []).filter((e) => e.tone === "yellow");

    return (
        <section
            className="mt-10 p-6 bg-cream-warm rounded-sm border border-forest/10"
            data-testid="admin-pricing-log"
        >
            <header className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <span className="divider-rule text-brown">
                        <Scale size={12} /> Pricing · Market Guardrail Log
                    </span>
                    <h3 className="font-serif text-2xl text-forest mt-2">
                        Audit · Red Overrides &amp; Yellow Nudges
                    </h3>
                    <p className="text-xs text-brown mt-1 max-w-xl">
                        Prices go live instantly — no approval. This is a
                        view-only log of every time a creator's price fell
                        outside the market range.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={load}
                    data-testid="admin-pricing-log-refresh"
                    className="btn-pill btn-ghost text-xs"
                >
                    Refresh
                </button>
            </header>

            {entries == null ? (
                <div className="py-10 flex justify-center">
                    <Loader2 size={22} className="animate-spin text-forest" />
                </div>
            ) : (
                <div className="mt-6 space-y-5">
                    <OverrideSection
                        title="Red overrides · reason on file"
                        entries={overrides}
                        emptyMsg="No red overrides — every creator price has stayed within market."
                        showReason
                        testId="admin-pricing-red-list"
                    />
                    <OverrideSection
                        title="Yellow soft nudges · no reason required"
                        entries={yellows}
                        emptyMsg="No yellow nudges logged."
                        showReason={false}
                        testId="admin-pricing-yellow-list"
                    />
                </div>
            )}
        </section>
    );
}

function OverrideSection({ title, entries, emptyMsg, showReason, testId }) {
    return (
        <div data-testid={testId}>
            <p className="text-[0.6rem] tracking-[0.3em] uppercase text-brown mb-2">
                {title}
            </p>
            {entries.length === 0 ? (
                <p className="text-sm text-brown italic">{emptyMsg}</p>
            ) : (
                <ul className="space-y-2">
                    {entries.map((e) => (
                        <li
                            key={e._id || `${e.slug}-${e.submitted_at}`}
                            className="p-3 bg-cream rounded-sm border border-forest/15 text-sm"
                        >
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="font-mono text-xs text-forest">
                                    {e.slug}
                                </span>
                                <span className="font-serif text-forest">
                                    {fmt(e.partner_price)}
                                </span>
                                <span className="text-[0.55rem] uppercase tracking-[0.2em] text-brown">
                                    {e.direction} · market {fmt(e.market_low)}–{fmt(e.market_high)}
                                </span>
                                <span className="ml-auto text-[0.6rem] text-brown">
                                    {fmtDate(e.submitted_at)}
                                </span>
                            </div>
                            {showReason && e.reason && (
                                <p className="mt-1 text-xs text-forest italic">
                                    "{e.reason}" — {e.submitted_by}
                                </p>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// =============================================================
// 2. AFFILIATE PARTNERS — Traffic lights, AI re-rate, red-flag reason gate
// =============================================================
function AffiliatePartnersAdmin() {
    const [partners, setPartners] = useState(null);
    const [edits, setEdits] = useState({});
    const [busy, setBusy] = useState({});
    const [rating, setRating] = useState(false);
    const [reasonModal, setReasonModal] = useState(null); // { slug, partner, nextStatus }
    const [log, setLog] = useState([]);

    const load = useCallback(async () => {
        try {
            const [{ data }, { data: logData }] = await Promise.all([
                api.get("/admin/affiliate-partners"),
                api.get("/admin/affiliate-partners/override-log"),
            ]);
            setPartners(data.partners);
            setLog(logData.entries);
            const seed = {};
            for (const p of data.partners) seed[p.slug] = p.url || "";
            setEdits(seed);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load partners");
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const saveUrl = async (slug) => {
        setBusy((b) => ({ ...b, [slug]: "save" }));
        try {
            await api.put(`/admin/affiliate-partners/${slug}`, { url: edits[slug] || "" });
            toast.success("Link saved");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setBusy((b) => ({ ...b, [slug]: null }));
        }
    };

    const flip = async (slug, nextStatus, overrideReason) => {
        setBusy((b) => ({ ...b, [slug]: "flip" }));
        try {
            const payload = { url: edits[slug] || "", status: nextStatus };
            if (overrideReason) payload.override_reason = overrideReason;
            await api.put(`/admin/affiliate-partners/${slug}`, payload);
            toast.success(
                nextStatus === "live"
                    ? "Live — the Partners page just updated"
                    : "Flipped back to Coming Soon",
            );
            setReasonModal(null);
            await load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.code === "override_reason_required") {
                toast.error(detail.message || "Reason required");
            } else {
                toast.error(typeof detail === "string" ? detail : "Status change failed");
            }
        } finally {
            setBusy((b) => ({ ...b, [slug]: null }));
        }
    };

    const requestFlip = (p) => {
        if (p.status === "live") {
            flip(p.slug, "coming_soon");
            return;
        }
        if (p.rating === "red") {
            setReasonModal({ slug: p.slug, partner: p, nextStatus: "live" });
            return;
        }
        flip(p.slug, "live");
    };

    const rate = async () => {
        setRating(true);
        try {
            const { data } = await api.post("/admin/affiliate-partners/rate", {
                hull_context: "Island Gold — Pacific Islander skincare & soap. Hawaii-based creator, clean ingredients, cultural authenticity.",
            });
            toast.success(`AI re-rated ${data.count} partners`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI rating failed");
        } finally {
            setRating(false);
        }
    };

    if (partners == null) {
        return (
            <div className="py-10 flex justify-center">
                <Loader2 size={22} className="animate-spin text-forest" />
            </div>
        );
    }

    return (
        <section
            className="mt-10 p-6 bg-cream-warm rounded-sm border border-forest/10"
            data-testid="admin-affiliate-partners"
        >
            <header className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <span className="divider-rule text-brown">
                        <Users size={12} /> Affiliate Partners · Trust Standard
                    </span>
                    <h3 className="font-serif text-2xl text-forest mt-2">
                        Flip Coming Soon → Live
                    </h3>
                    <p className="text-xs text-brown mt-1 max-w-xl">
                        AI-rated 🟢🟡🔴 ·  Reds require a reason before going
                        live. Public page shows green + yellow as "Curated"
                        only.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={rate}
                        disabled={rating}
                        data-testid="admin-affiliate-rerate"
                        className="btn-pill btn-gold text-xs disabled:opacity-40"
                    >
                        {rating ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}{" "}
                        Re-rate (AI)
                    </button>
                    <button
                        type="button"
                        onClick={load}
                        data-testid="admin-affiliate-refresh"
                        className="btn-pill btn-ghost text-xs"
                    >
                        Refresh
                    </button>
                </div>
            </header>

            <div className="mt-6 space-y-2">
                {partners.map((p) => {
                    const isLive = p.status === "live";
                    return (
                        <article
                            key={p.slug}
                            className="p-4 bg-cream border border-forest/15 rounded-sm grid md:grid-cols-12 gap-3 items-center"
                            data-testid={`admin-partner-row-${p.slug}`}
                        >
                            <div className="md:col-span-4">
                                <div className="flex items-center gap-2">
                                    <TrafficLightBadge
                                        rating={p.rating}
                                        size="sm"
                                        testId={`admin-partner-rating-${p.slug}`}
                                    />
                                    <p className="font-serif text-base text-forest leading-tight">
                                        {p.name}
                                    </p>
                                </div>
                                <p className="text-[0.55rem] tracking-[0.25em] uppercase text-brown mt-1">
                                    {p.section} · {p.tag}
                                </p>
                                {p.recommendation && (
                                    <p
                                        className="text-xs text-brown italic mt-1 leading-snug"
                                        data-testid={`admin-partner-rec-${p.slug}`}
                                    >
                                        {p.recommendation}
                                    </p>
                                )}
                            </div>
                            <div className="md:col-span-5">
                                <div className="relative">
                                    <LinkIcon size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-brown" />
                                    <input
                                        type="url"
                                        value={edits[p.slug] ?? ""}
                                        onChange={(e) =>
                                            setEdits((d) => ({ ...d, [p.slug]: e.target.value }))
                                        }
                                        placeholder="https://brand.com?affiliate=…"
                                        data-testid={`admin-partner-url-${p.slug}`}
                                        className="w-full pl-9 pr-3 py-2 border border-forest/20 rounded-sm text-sm text-forest focus:outline-none focus:border-gold font-mono"
                                    />
                                </div>
                            </div>
                            <div className="md:col-span-3 flex gap-2 justify-end">
                                <button
                                    type="button"
                                    onClick={() => saveUrl(p.slug)}
                                    disabled={busy[p.slug] === "save"}
                                    data-testid={`admin-partner-save-${p.slug}`}
                                    className="btn-pill btn-ghost text-[0.65rem]"
                                >
                                    {busy[p.slug] === "save" ? <Loader2 size={11} className="animate-spin" /> : "Save"}
                                </button>
                                {isLive ? (
                                    <button
                                        type="button"
                                        onClick={() => requestFlip(p)}
                                        disabled={busy[p.slug] === "flip"}
                                        data-testid={`admin-partner-unlive-${p.slug}`}
                                        className="btn-pill text-[0.65rem] bg-emerald-700 text-cream border border-emerald-500"
                                    >
                                        <Globe size={11} /> Live
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => requestFlip(p)}
                                        disabled={busy[p.slug] === "flip" || !edits[p.slug]}
                                        data-testid={`admin-partner-golive-${p.slug}`}
                                        className="btn-pill btn-gold text-[0.65rem] disabled:opacity-40"
                                    >
                                        {busy[p.slug] === "flip" ? (
                                            <Loader2 size={11} className="animate-spin" />
                                        ) : (
                                            <>
                                                <Rocket size={11} /> {p.rating === "red" ? "Confirm & Live" : "Make Live"}
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </article>
                    );
                })}
            </div>

            {/* Red-flag override log */}
            {log.length > 0 && (
                <div className="mt-8" data-testid="admin-partner-override-log">
                    <p className="text-[0.6rem] tracking-[0.3em] uppercase text-brown mb-2">
                        Red Overrides on File
                    </p>
                    <ul className="space-y-2">
                        {log.map((e) => (
                            <li
                                key={e._id || `${e.slug}-${e.submitted_at}`}
                                className="p-3 bg-cream rounded-sm border border-forest/15 text-sm"
                            >
                                <div className="flex flex-wrap items-center gap-2">
                                    <TrafficLightBadge rating="red" size="sm" />
                                    <span className="font-mono text-xs text-forest">{e.slug}</span>
                                    <span className="ml-auto text-[0.6rem] text-brown">
                                        {fmtDate(e.submitted_at)}
                                    </span>
                                </div>
                                <p className="mt-1 text-xs text-forest italic">
                                    "{e.reason}" — {e.submitted_by}
                                </p>
                                {e.rating_rationale && (
                                    <p className="text-[0.65rem] text-brown mt-1">
                                        AI said: {e.rating_rationale}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <OverrideReasonModal
                open={!!reasonModal}
                title={`Activate a red-flagged partner`}
                subtitle={
                    reasonModal
                        ? `${reasonModal.partner.name} is currently rated low priority for your niche.`
                        : ""
                }
                aiRationale={reasonModal?.partner?.rating_rationale || reasonModal?.partner?.recommendation}
                placeholder="e.g. We've verified the brand directly and the fit is better than the AI suggests."
                confirmLabel="Activate Partner"
                tone="rose"
                busy={!!(reasonModal && busy[reasonModal.slug] === "flip")}
                onCancel={() => setReasonModal(null)}
                onConfirm={(reason) => flip(reasonModal.slug, "live", reason)}
                testId="partner-override-modal"
            />
        </section>
    );
}

// =============================================================
// 3. INSIDER PREVIEW DROP · unchanged
// =============================================================
function InsiderPreviewAdmin() {
    const [state, setState] = useState(null);
    const [products, setProducts] = useState([]);
    const [busy, setBusy] = useState({});

    const load = useCallback(async () => {
        try {
            const [{ data: rel }, { data: p }] = await Promise.all([
                api.get("/release-state"),
                api.get("/products"),
            ]);
            setState(rel.release_state);
            setProducts(p.products);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load release state");
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const release = async (slug) => {
        setBusy((b) => ({ ...b, [slug]: true }));
        try {
            const { data } = await api.post(`/admin/products/${slug}/release`, { hours: 48 });
            toast.success(`Dropped · insiders only until ${new Date(data.early_access_ends_at).toLocaleString()}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Release failed");
        } finally {
            setBusy((b) => ({ ...b, [slug]: false }));
        }
    };

    return (
        <section
            className="mt-10 p-6 bg-cream-warm rounded-sm border border-forest/10"
            data-testid="admin-insider-preview"
        >
            <header className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <span className="divider-rule text-brown">
                        <Radio size={12} /> Insider Preview Drop
                    </span>
                    <h3 className="font-serif text-2xl text-forest mt-2">
                        Release · 48h waitlist-only window
                    </h3>
                </div>
                <button
                    type="button"
                    onClick={load}
                    data-testid="admin-insider-refresh"
                    className="btn-pill btn-ghost text-xs"
                >
                    Refresh
                </button>
            </header>

            {state == null ? (
                <div className="py-10 flex justify-center">
                    <Loader2 size={22} className="animate-spin text-forest" />
                </div>
            ) : (
                <div className="mt-6 space-y-2">
                    {products.map((p) => {
                        const s = state[p.slug] || { in_window: false, early_access_ends_at: null };
                        const hoursLeft = s.in_window ? remainingHours(s.early_access_ends_at) : 0;
                        return (
                            <article
                                key={p.slug}
                                className="p-4 bg-cream border border-forest/15 rounded-sm grid md:grid-cols-12 gap-3 items-center"
                                data-testid={`admin-release-row-${p.slug}`}
                            >
                                <div className="md:col-span-5 flex gap-3 items-center">
                                    <img src={p.image} alt={p.name} className="w-12 h-12 object-cover rounded-sm shrink-0" />
                                    <div className="min-w-0">
                                        <p className="font-serif text-base text-forest">{p.name}</p>
                                        <p className="text-[0.55rem] tracking-[0.25em] uppercase text-brown mt-1">{fmt(p.price)}</p>
                                    </div>
                                </div>
                                <div className="md:col-span-4">
                                    {s.in_window ? (
                                        <div
                                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold/40 bg-gold/10 text-gold text-[0.6rem] tracking-[0.2em] uppercase"
                                            data-testid={`admin-release-status-${p.slug}`}
                                        >
                                            <Lock size={10} /> Insiders · {hoursLeft.toFixed(1)}h left
                                        </div>
                                    ) : s.released_at ? (
                                        <div
                                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/40 bg-emerald-900/15 text-emerald-700 text-[0.6rem] tracking-[0.2em] uppercase"
                                            data-testid={`admin-release-status-${p.slug}`}
                                        >
                                            <Globe size={10} /> Open to Everyone
                                        </div>
                                    ) : (
                                        <div
                                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brown/40 bg-cream text-brown text-[0.6rem] tracking-[0.2em] uppercase"
                                            data-testid={`admin-release-status-${p.slug}`}
                                        >
                                            <Clock size={10} /> Not Released
                                        </div>
                                    )}
                                </div>
                                <div className="md:col-span-3 flex justify-end">
                                    <button
                                        type="button"
                                        onClick={() => release(p.slug)}
                                        disabled={busy[p.slug]}
                                        data-testid={`admin-release-btn-${p.slug}`}
                                        className="btn-pill btn-gold text-[0.65rem] disabled:opacity-40"
                                    >
                                        {busy[p.slug] ? (
                                            <Loader2 size={11} className="animate-spin" />
                                        ) : (
                                            <>
                                                <Rocket size={11} /> {s.in_window ? "Restart 48h" : "Release"}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </section>
    );
}

export default function AdminFeaturesPanel() {
    return (
        <>
            <PricingOverrideLog />
            <AffiliatePartnersAdmin />
            <InsiderPreviewAdmin />
        </>
    );
}

export { PricingOverrideLog, AffiliatePartnersAdmin, InsiderPreviewAdmin };
